import {
  archivedToolCategoriesQuery,
  toolCategoriesQuery,
} from "@common/api/tool-categories"
import type { ToolCategoryPublic } from "@common/client"
import { Button } from "@common/components/ui/button"
import { GridTable } from "@common/components/ui/grid-table"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import { Tag } from "lucide-react"
import { z } from "zod"
import { FilterBar } from "@/components/layout/filter-bar"
import { ListEmpty } from "@/components/layout/list-empty"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import {
  SettingsTableCell,
  SettingsTableHead,
  SettingsTableHeader,
  SettingsTableRow,
} from "@/routes/_layout/settings/_panel/-components/settings-table"

const COLUMNS = "grid-cols-[minmax(0,1fr)]"

const filterSchema = z.object({
  archived: z.boolean().optional().catch(false),
  search: z.string().optional().catch(""),
})

export const Route = createFileRoute(
  "/_layout/settings/_panel/workspace/tool-categories/_list",
)({
  validateSearch: (search) => filterSchema.parse(search),
  loaderDeps: ({ search }) => ({ archived: search.archived }),
  loader: ({ context: { queryClient }, deps }) =>
    queryClient.ensureQueryData(
      deps.archived ? archivedToolCategoriesQuery() : toolCategoriesQuery(),
    ),
  component: CategoriesPage,
})

function CategoriesPage(): React.ReactElement {
  const { search = "" } = Route.useSearch()

  return (
    <div className="flex h-full flex-col">
      <FilterBar>
        <Toolbar search={search} />
      </FilterBar>
      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-y-auto [--filter-bar-height:0px]">
          <CategoryResults search={search} />
        </div>
        <Outlet />
      </div>
    </div>
  )
}

function Toolbar({ search }: { search: string }): React.ReactElement {
  const navigate = useNavigate({ from: Route.fullPath })
  const { archived = false } = Route.useSearch()
  const canCreate = useHasPermission("tools:create")
  const filter = (next: string) =>
    navigate({
      search: (previous) => ({ ...previous, search: next || undefined }),
      replace: true,
    })

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={["active", "archived"] as const}
          tab={archived ? "archived" : "active"}
          label={(tab) => (tab === "active" ? "Active" : "Archived")}
          name="Record status"
          onSelect={(tab) =>
            navigate({
              search: (previous) => ({
                ...previous,
                archived: tab === "archived" || undefined,
              }),
              replace: true,
            })
          }
        />
      }
      actions={
        <>
          <NameFilter search={search} onSearch={filter} />
          {canCreate && (
            <Button asChild size="sm">
              <Link to="/settings/workspace/tool-categories/create">
                New category
              </Link>
            </Button>
          )}
        </>
      }
    />
  )
}

function CategoryResults({ search }: { search: string }): React.ReactElement {
  const { archived = false } = Route.useSearch()
  const { data } = useSuspenseQuery(
    archived ? archivedToolCategoriesQuery() : toolCategoriesQuery(),
  )
  const params = useParams({ strict: false })
  const categoryId = "categoryId" in params ? params.categoryId : undefined
  const needle = search.toLowerCase()
  const categories = data.data.filter((category) =>
    category.name.toLowerCase().includes(needle),
  )

  if (categories.length === 0) {
    return <EmptyState search={search} archived={archived} />
  }

  return (
    <GridTable className={COLUMNS}>
      <SettingsTableHeader>
        <SettingsTableHead className="pl-6">Name</SettingsTableHead>
      </SettingsTableHeader>
      {categories.map((category) => (
        <CategoryRow
          key={category.id}
          category={category}
          selected={category.id === categoryId}
        />
      ))}
    </GridTable>
  )
}

function CategoryRow({
  category,
  selected,
}: {
  category: ToolCategoryPublic
  selected: boolean
}): React.ReactElement {
  return (
    <SettingsTableRow
      asChild
      archived={Boolean(category.deleted_at)}
      aria-selected={selected}
      selected={selected}
    >
      <Link
        to="/settings/workspace/tool-categories/$categoryId"
        params={{ categoryId: category.id }}
        search={true}
      >
        <SettingsTableCell className="pl-6">
          <span className="truncate">{category.name}</span>
        </SettingsTableCell>
      </Link>
    </SettingsTableRow>
  )
}

function EmptyState({
  search,
  archived,
}: {
  search: string
  archived: boolean
}): React.ReactElement {
  const title = search
    ? "No matching categories"
    : archived
      ? "No archived categories"
      : "No tool categories"
  const body = search
    ? "Try a different name."
    : archived
      ? "Archived categories appear here."
      : "Add categories to group your tools, like torque or NDT."

  return <ListEmpty icon={Tag} title={title} body={body} />
}
